import { ArrowLeft, Copy, Check, Pencil, Save, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import AudioPlayer from "./AudioPlayer";
import {
  EVAL_SLOTS,
  DAILY_SLOTS,
  getTherapyConfig,
  readSlot,
} from "../../lib/therapyTypes";

const PATIENT_FIELDS = [
  { key: "transcript", label: "Patient narrative (raw)" },
  { key: "summary", label: "AI summary" },
];

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function FieldRow({ label, value, onSaveEdit }) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Couldn't copy", { description: e.message });
    }
  };

  const startEdit = () => {
    setDraft(value || "");
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft(value || "");
  };
  const saveEdit = async () => {
    if (!onSaveEdit) return setEditing(false);
    setSaving(true);
    try {
      await onSaveEdit(draft);
      toast.success(`${label} updated`);
      setEditing(false);
    } catch (e) {
      toast.error("Couldn't save", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`readonly-field-${slug}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="font-figtree text-[11px] uppercase tracking-[0.18em] font-semibold text-[#059669]">
          {label}
        </div>
        <div className="flex items-center gap-1">
          {onSaveEdit && !editing && (
            <button
              data-testid={`readonly-field-${slug}-edit`}
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-figtree text-xs font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] transition-all"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
          <button
            data-testid={`readonly-field-${slug}-copy`}
            onClick={handleCopy}
            disabled={!value || editing}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-figtree text-xs font-medium transition-all ${
              copied
                ? "bg-[#D1FAE5] text-[#065F46]"
                : "text-[#64748B] hover:text-[#059669] hover:bg-[#F0FDF4] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#64748B]"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>
      {editing ? (
        <div>
          <Textarea
            data-testid={`readonly-field-${slug}-editor`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(12, Math.max(4, draft.split("\n").length + 1))}
            className="font-figtree text-sm bg-white border-[#A7F3D0] focus-visible:ring-[#10B981] rounded-xl"
          />
          <div className="mt-2 flex gap-2 justify-end">
            <Button
              data-testid={`readonly-field-${slug}-cancel`}
              onClick={cancelEdit}
              variant="outline"
              className="rounded-full border-slate-200 font-figtree text-xs"
              disabled={saving}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Cancel
            </Button>
            <Button
              data-testid={`readonly-field-${slug}-save`}
              onClick={saveEdit}
              className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white font-figtree text-xs font-medium"
              disabled={saving}
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="font-figtree text-sm text-[#0F172A] leading-relaxed whitespace-pre-wrap bg-[#F8FAFC] rounded-xl p-4 border border-slate-100 min-h-[60px]">
          {value || (
            <span className="text-[#94A3B8] italic">No content recorded.</span>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlanReadOnlyDialog({ plan, patient, onClose, onUpdate, originLabel, therapyType = "physiotherapy", canEdit = false }) {
  if (!plan) return null;

  const cfg = getTherapyConfig(plan.therapy_type || therapyType);
  const EVAL_FIELDS = cfg.evaluation.map((label, i) => ({
    key: EVAL_SLOTS[i],
    label,
    index: i,
  }));
  const DAILY_FIELDS = cfg.daily.map((label, i) => ({
    key: DAILY_SLOTS[i],
    label,
    index: i,
  }));

  const makeEditor = (section, key) => {
    if (!canEdit || !onUpdate) return undefined;
    return async (newValue) => {
      await onUpdate(plan.id, section, key, newValue);
    };
  };

  const defaultTab =
    plan.type === "evaluation"
      ? plan.patient?.transcript || plan.patient?.summary
        ? "patient"
        : "evaluation"
      : "daily";

  return (
    <Dialog open={!!plan} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="plan-readonly-dialog"
        className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto bg-white rounded-3xl border-slate-200"
      >
        <DialogHeader>
          <button
            onClick={onClose}
            data-testid="plan-readonly-back-btn"
            className="inline-flex items-center gap-2 font-figtree text-sm text-[#475569] hover:text-[#0F172A] transition-colors mb-2 self-start"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {originLabel}
          </button>
          <div className="flex items-center gap-3">
            {patient && (
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#D1FAE5] text-[#065F46] font-outfit text-sm font-bold">
                {patient.initials}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <DialogTitle className="font-outfit text-xl font-bold tracking-tight">
                {patient?.id || "Patient"} · {formatDate(plan.date)}
              </DialogTitle>
              <p className="font-figtree text-xs text-[#64748B]">
                {patient?.initials ? `Initials: ${patient.initials}` : ""}
              </p>
            </div>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                plan.type === "evaluation"
                  ? "bg-[#0F172A] text-white"
                  : "bg-[#D1FAE5] text-[#065F46]"
              }`}
            >
              {plan.type === "evaluation" ? "Evaluation" : "Daily Note"}
            </span>
          </div>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="mt-2">
          <TabsList className="bg-slate-100 rounded-full p-1 h-auto">
            <TabsTrigger
              value="patient"
              data-testid="readonly-tab-patient"
              className="rounded-full font-figtree text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Patient Notes
            </TabsTrigger>
            <TabsTrigger
              value="evaluation"
              data-testid="readonly-tab-evaluation"
              className="rounded-full font-figtree text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Evaluation
            </TabsTrigger>
            <TabsTrigger
              value="daily"
              data-testid="readonly-tab-daily"
              className="rounded-full font-figtree text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              Daily Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="patient" className="mt-4 space-y-4">
            {plan.patient?.audioUrl && (
              <AudioPlayer src={plan.patient.audioUrl} label="Patient Notes audio" mode="saved" />
            )}
            {PATIENT_FIELDS.map((f) => (
              <FieldRow
                key={f.key}
                label={f.label}
                value={plan.patient?.[f.key]}
                onSaveEdit={makeEditor("patient", f.key)}
              />
            ))}
          </TabsContent>

          <TabsContent value="evaluation" className="mt-4 space-y-4">
            {plan.evaluation?.audioUrl && (
              <AudioPlayer src={plan.evaluation.audioUrl} label="Evaluation audio" mode="saved" />
            )}
            {EVAL_FIELDS.map((f) => (
              <FieldRow
                key={f.key}
                label={f.label}
                value={readSlot(plan.evaluation, f.key, f.index, "evaluation")}
                onSaveEdit={makeEditor("evaluation", f.key)}
              />
            ))}
          </TabsContent>

          <TabsContent value="daily" className="mt-4 space-y-4">
            {plan.daily?.audioUrl && (
              <AudioPlayer src={plan.daily.audioUrl} label="Daily Notes audio" mode="saved" />
            )}
            {DAILY_FIELDS.map((f) => (
              <FieldRow
                key={f.key}
                label={f.label}
                value={readSlot(plan.daily, f.key, f.index, "daily")}
                onSaveEdit={makeEditor("daily", f.key)}
              />
            ))}
          </TabsContent>
        </Tabs>

        <div className="mt-4 flex justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            data-testid="plan-readonly-close-btn"
            className="rounded-full font-figtree text-sm border-slate-200"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
